import { useEffect, useMemo, useState, useRef } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { Search, Phone, Menu, ClipboardList, X, ChevronDown, ChevronRight, Layers, ArrowRight } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "../ui/dialog";
import { Sheet, SheetContent, SheetTrigger } from "../ui/sheet";
import { NAV_LINKS, pimg, formatCategoryName } from "../../lib/site";
import { api } from "../../lib/api";
import { useInquiry } from "../../context/InquiryContext";
import { useSettings } from "../../context/SettingsContext";

const SearchDialog = ({ open, onOpenChange }) => {
  const [products, setProducts] = useState([]);
  const [q, setQ] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    if (open && products.length === 0) {
      api.get("/products").then((r) => setProducts(r.data)).catch(() => { });
    }
  }, [open, products.length]);

  const results = q
    ? products.filter(
      (p) =>
        p.name.toLowerCase().includes(q.toLowerCase()) ||
        p.category.toLowerCase().includes(q.toLowerCase())
    )
    : products;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none border-brand-ink sm:max-w-xl">
        <DialogTitle className="mono-caption text-neutral-500">Search Products</DialogTitle>
        <input
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Paddles, balls, nets..."
          data-testid="search-input"
          className="input-pp"
        />
        <div className="max-h-80 overflow-y-auto divide-y divide-black/5">
          {results.map((p) => (
            <button
              key={p.id}
              data-testid={`search-result-${p.id}`}
              className="flex w-full items-center gap-4 py-3 text-left hover:bg-brand-smoke px-2 transition-colors"
              onClick={() => {
                onOpenChange(false);
                setQ("");
                navigate(`/products/${p.id}`);
              }}
            >
              <img src={pimg(p.images[0], 120, 120)} alt="" className="h-12 w-12 object-cover" />
              <div>
                <p className="font-display font-medium">{p.name}</p>
                <p className="font-mono text-[11px] uppercase tracking-wider text-neutral-400">
                  {formatCategoryName(p.category)} — MOQ {p.moq}
                </p>
              </div>
            </button>
          ))}
          {results.length === 0 && (
            <p className="py-8 text-center text-sm text-neutral-400">No products found.</p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const getCachedNavCategories = () => {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem("pbg_home_categories");
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed;
    }
  } catch (_) {}
  return [];
};

const Navbar = () => {
  const [searchOpen, setSearchOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [categoriesOpen, setCategoriesOpen] = useState(false);
  const [mobileCategoriesExpanded, setMobileCategoriesExpanded] = useState(true);
  const [categories, setCategories] = useState(getCachedNavCategories);
  const dropdownTimeoutRef = useRef(null);
  const location = useLocation();
  const { count } = useInquiry();
  const site = useSettings();

  useEffect(() => {
    api.get("/categories")
      .then((r) => {
        if (Array.isArray(r.data)) {
          setCategories(r.data);
          try {
            localStorage.setItem("pbg_home_categories", JSON.stringify(r.data));
          } catch (_) {}
        }
      })
      .catch(() => {});
  }, []);

  // Close dropdown on route changes
  useEffect(() => {
    setCategoriesOpen(false);
  }, [location.pathname]);

  const navLinks = useMemo(() => {
    if (Array.isArray(site.navigationMenu) && site.navigationMenu.length > 0) {
      return site.navigationMenu
        .filter((item) => item.is_active !== false)
        .map((item) => ({
          to: item.url || "/",
          label: item.label || "Link",
          type: item.type || "standard",
          openInNewTab: Boolean(item.open_in_new_tab),
          subItems: Array.isArray(item.sub_items) ? item.sub_items.filter((s) => s.is_active !== false) : [],
        }));
    }
    return NAV_LINKS.map((item) => ({
      ...item,
      type: item.to === "/products" ? "products_dropdown" : "standard",
    }));
  }, [site.navigationMenu]);

  // Build hierarchical category tree (Parent Categories -> Subcategories)
  const categoryTree = useMemo(() => {
    if (!Array.isArray(categories) || categories.length === 0) return [];
    
    // Parent categories have parent_id == null, 0, "0", or ""
    const isParent = (c) => !c.parent_id || c.parent_id === 0 || c.parent_id === "0" || c.parent_id === "null";
    
    const parents = categories
      .filter(isParent)
      .sort((a, b) => (Number(a.sort_order) || 0) - (Number(b.sort_order) || 0) || Number(a.id) - Number(b.id));
    
    return parents.map((parent) => {
      const subs = categories
        .filter((c) => !isParent(c) && Number(c.parent_id) === Number(parent.id))
        .sort((a, b) => (Number(a.sort_order) || 0) - (Number(b.sort_order) || 0) || Number(a.id) - Number(b.id));
      return {
        ...parent,
        subcategories: subs,
      };
    });
  }, [categories]);

  const handleMouseEnter = () => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
    }
    setCategoriesOpen(true);
  };

  const handleMouseLeave = () => {
    dropdownTimeoutRef.current = setTimeout(() => {
      setCategoriesOpen(false);
    }, 180);
  };

  return (
    <>
      {site.enablePromoNotice && (
        <div className="bg-brand-ink text-white py-2 px-4 text-center font-mono text-[11px] uppercase tracking-wider border-b border-white/10 flex items-center justify-center gap-2" data-testid="navbar-promo-notice">
          <span>{site.promoNoticeText}</span>
          {site.promoNoticeUrl && (
            <Link to={site.promoNoticeUrl} className="text-brand-red font-semibold hover:underline ml-1">
              Details &rarr;
            </Link>
          )}
        </div>
      )}
      <header className="sticky top-0 z-50 w-full border-b border-black/10 bg-white/90 backdrop-blur-xl">
        <div className="mx-auto flex h-16 md:h-20 max-w-[1400px] items-center justify-between px-5 lg:px-10">
          <Link to="/" data-testid="nav-logo" className="flex items-center">
            <img
              src="/logo-black.webp"
              alt={site.name}
              className="h-14 md:h-16 w-auto object-contain"
            />
          </Link>

          <nav className="hidden items-center gap-7 xl:flex" data-testid="nav-desktop">
            {navLinks.map((l) => {
              const isProductsDropdown = l.type === "products_dropdown" || l.to === "/products";
              const hasCustomSubmenu = l.type === "custom_dropdown" && l.subItems.length > 0;

              if (isProductsDropdown) {
                return (
                  <div
                    key={l.to}
                    className="relative"
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                  >
                    <NavLink
                      to={l.to}
                      data-testid="nav-link-products"
                      className={({ isActive }) =>
                        `group flex items-center gap-1.5 py-1.5 font-mono text-[13px] font-bold uppercase tracking-[0.14em] transition-colors ${
                          isActive || categoriesOpen ? "text-brand-red" : "text-brand-ink hover:text-brand-red"
                        }`
                      }
                    >
                      <span>{l.label}</span>
                      <ChevronDown
                        size={14}
                        className={`transition-transform duration-200 ${categoriesOpen ? "rotate-180 text-brand-red" : ""}`}
                      />
                      <span
                        className={`absolute bottom-0 left-0 h-[2px] w-full bg-brand-red transition-all duration-300 origin-left ${
                          categoriesOpen ? "scale-x-100 opacity-100" : "scale-x-0 opacity-0 group-hover:scale-x-100 group-hover:opacity-100"
                        }`}
                      />
                    </NavLink>

                    {/* Desktop Mega-Menu Dropdown */}
                    {categoriesOpen && (
                      <div
                        className="absolute left-1/2 -translate-x-1/2 top-full pt-3 w-[960px] max-w-[95vw] z-50 animate-in fade-in slide-in-from-top-2 duration-200"
                        onMouseEnter={handleMouseEnter}
                        onMouseLeave={handleMouseLeave}
                      >
                        <div className="bg-white border border-black/10 shadow-2xl overflow-hidden p-6 md:p-8">
                          {/* Dropdown Header Bar */}
                          <div className="flex items-center justify-between border-b border-black/10 pb-4 mb-6">
                            <div className="flex items-center gap-2">
                              <Layers size={16} className="text-brand-red" />
                              <span className="font-mono text-xs uppercase font-bold tracking-widest text-brand-ink">
                                {site.navigationSettings?.products_dropdown_title || "Browse by Category"}
                              </span>
                            </div>
                            <Link
                              to="/products"
                              onClick={() => setCategoriesOpen(false)}
                              className="font-mono text-[11px] font-bold uppercase tracking-wider text-brand-red hover:underline flex items-center gap-1"
                            >
                              {site.navigationSettings?.products_dropdown_view_all_text || "View Full Catalog →"}
                            </Link>
                          </div>

                          {/* Categories Hierarchy Grid (Clean Typography, Bold Parents, Non-Bold Subs) */}
                          <div className={`grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 ${categoryTree.length >= 6 ? 'lg:grid-cols-6' : 'lg:grid-cols-5'} gap-x-6 gap-y-7 items-start`}>
                            {categoryTree.map((parent) => (
                              <div key={parent.id} className="group/col space-y-2">
                                {/* Parent Category Header (Bold) */}
                                <div className="border-b border-black/10 pb-1.5 transition-colors group-hover/col:border-brand-red">
                                  <Link
                                    to={`/products?category=${encodeURIComponent(parent.name)}`}
                                    onClick={() => setCategoriesOpen(false)}
                                    className="group/parent flex items-center justify-between text-brand-ink hover:text-brand-red transition-colors"
                                  >
                                    <span className="font-display text-[13px] font-bold uppercase tracking-wide leading-tight">
                                      {formatCategoryName(parent.name)}
                                    </span>
                                    <ChevronRight size={12} className="opacity-0 -translate-x-1 group-hover/parent:opacity-100 group-hover/parent:translate-x-0 transition-all text-brand-red shrink-0" />
                                  </Link>
                                </div>

                                {/* Child Subcategories (Normal weight, clean list) */}
                                {parent.subcategories && parent.subcategories.length > 0 ? (
                                  <ul className="space-y-1 pt-0.5">
                                    {parent.subcategories.map((sub) => (
                                      <li key={sub.id}>
                                        <Link
                                          to={`/products?category=${encodeURIComponent(sub.name)}`}
                                          onClick={() => setCategoriesOpen(false)}
                                          className="group/sub flex items-center justify-between py-0.5 text-[12px] font-normal text-neutral-600 hover:text-brand-red transition-all"
                                        >
                                          <span className="flex items-center gap-1.5 leading-snug">
                                            <span className="h-1 w-1 rounded-full bg-neutral-300 group-hover/sub:bg-brand-red group-hover/sub:scale-125 transition-all shrink-0" />
                                            <span className="group-hover/sub:translate-x-0.5 transition-transform">{formatCategoryName(sub.name)}</span>
                                          </span>
                                          {sub.count > 0 && (
                                            <span className="font-mono text-[10px] text-neutral-400 group-hover/sub:text-brand-red">
                                              ({sub.count})
                                            </span>
                                          )}
                                        </Link>
                                      </li>
                                    ))}
                                  </ul>
                                ) : (
                                  <div className="pt-0.5">
                                    <Link
                                      to={`/products?category=${encodeURIComponent(parent.name)}`}
                                      onClick={() => setCategoriesOpen(false)}
                                      className="inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-wider text-neutral-400 hover:text-brand-red transition-colors"
                                    >
                                      <span>Explore</span>
                                      <ArrowRight size={10} />
                                    </Link>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>

                          {/* Bottom OEM Highlight Bar */}
                          <div className="mt-8 pt-4 border-t border-black/5 bg-brand-smoke -mx-6 md:-mx-8 -mb-6 md:-mb-8 px-6 md:px-8 py-3 flex items-center justify-between text-xs text-neutral-600">
                            <span className="font-mono text-[11px] uppercase tracking-wider">
                              OEM / ODM Private Label Manufacturing & Custom Tooling Available
                            </span>
                            <Link
                              to="/inquiry"
                              onClick={() => setCategoriesOpen(false)}
                              className="font-mono text-[11px] font-bold uppercase tracking-wider text-brand-ink hover:text-brand-red flex items-center gap-1"
                            >
                              Request Custom Quote <ArrowRight size={12} />
                            </Link>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              }

              if (hasCustomSubmenu) {
                return (
                  <div key={l.to} className="relative group/custom">
                    <NavLink
                      to={l.to}
                      className={({ isActive }) =>
                        `group flex items-center gap-1.5 py-1.5 font-mono text-[13px] font-bold uppercase tracking-[0.14em] transition-colors ${
                          isActive ? "text-brand-red" : "text-brand-ink hover:text-brand-red"
                        }`
                      }
                    >
                      <span>{l.label}</span>
                      <ChevronDown size={14} className="group-hover/custom:rotate-180 transition-transform duration-200" />
                    </NavLink>
                    <div className="absolute left-0 top-full pt-2 hidden group-hover/custom:block z-50 min-w-[200px]">
                      <div className="bg-white border border-black/10 shadow-xl py-2 divide-y divide-black/5">
                        {l.subItems.map((sub, sIdx) => (
                          <Link
                            key={sIdx}
                            to={sub.url}
                            className="block px-4 py-2.5 text-xs font-mono font-medium uppercase tracking-wider text-brand-ink hover:text-brand-red hover:bg-brand-smoke transition-colors"
                          >
                            {sub.label}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              }

              return (
                <NavLink
                  key={l.to}
                  to={l.to}
                  target={l.openInNewTab ? "_blank" : undefined}
                  rel={l.openInNewTab ? "noopener noreferrer" : undefined}
                  data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `group relative py-1.5 font-mono text-[13px] font-bold uppercase tracking-[0.14em] transition-colors ${
                      isActive ? "text-brand-red" : "text-brand-ink hover:text-brand-red"
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      <span>{l.label}</span>
                      <span
                        className={`absolute bottom-0 left-0 h-[2px] w-full bg-brand-red transition-all duration-300 origin-left ${
                          isActive ? "scale-x-100 opacity-100" : "scale-x-0 opacity-0 group-hover:scale-x-100 group-hover:opacity-100"
                        }`}
                      />
                    </>
                  )}
                </NavLink>
              );
            })}
          </nav>

          <div className="flex items-center gap-2 md:gap-3">
            <button
              onClick={() => setSearchOpen(true)}
              data-testid="nav-search-btn"
              aria-label="Search products"
              className="flex h-10 w-10 items-center justify-center border border-transparent text-brand-ink transition-colors hover:border-brand-ink"
            >
              <Search size={18} />
            </button>
            <a
              href={site.phoneLink}
              data-testid="nav-call-btn"
              aria-label="Call us"
              className="hidden h-10 w-10 items-center justify-center border border-transparent text-brand-ink transition-colors hover:border-brand-ink md:flex"
            >
              <Phone size={18} />
            </a>
            <Link
              to="/inquiry"
              data-testid="nav-inquiry-btn"
              className="btn-primary relative !h-10 !px-4 !text-[11px]"
            >
              <ClipboardList size={14} />
              <span className="hidden sm:inline">Send Inquiry</span>
              {count > 0 && (
                <span
                  data-testid="inquiry-count-badge"
                  className="absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center bg-brand-ink font-mono text-[10px] text-white"
                >
                  {count}
                </span>
              )}
            </Link>
            <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
              <SheetTrigger asChild>
                <button
                  data-testid="mobile-menu-btn"
                  aria-label="Open menu"
                  className="flex h-10 w-10 items-center justify-center text-brand-ink xl:hidden"
                >
                  <Menu size={22} />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="w-full rounded-none border-l-brand-ink bg-white p-0 sm:max-w-md overflow-y-auto">
                <div className="flex h-16 items-center justify-between border-b border-black/10 px-6 sticky top-0 bg-white z-10">
                  <img src="/logo-black.webp" alt={site.name} className="h-8 w-auto object-contain" />
                  <button onClick={() => setMenuOpen(false)} aria-label="Close menu">
                    <X size={22} />
                  </button>
                </div>
                <nav className="flex flex-col p-6">
                  {navLinks.map((l, i) => {
                    const isProducts = l.type === "products_dropdown" || l.to === "/products";

                    if (isProducts) {
                      return (
                        <div key={l.to} className="border-b border-black/5 py-4">
                          <div className="flex items-center justify-between">
                            <Link
                              to={l.to}
                              onClick={() => setMenuOpen(false)}
                              data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                              className="flex items-baseline gap-4 font-display text-2xl font-medium tracking-tight hover:text-brand-red"
                            >
                              <span className="font-mono text-xs text-brand-red">0{i + 1}</span>
                              {l.label}
                            </Link>
                            <button
                              type="button"
                              onClick={() => setMobileCategoriesExpanded(!mobileCategoriesExpanded)}
                              aria-label="Toggle categories submenu"
                              className="p-2 text-neutral-500 hover:text-brand-red"
                            >
                              <ChevronDown
                                size={20}
                                className={`transition-transform duration-200 ${mobileCategoriesExpanded ? "rotate-180" : ""}`}
                              />
                            </button>
                          </div>

                          {/* Mobile Categories Accordion */}
                          {mobileCategoriesExpanded && categoryTree.length > 0 && (
                            <div className="mt-3 pl-8 space-y-3 pt-2 border-t border-black/5">
                              {categoryTree.map((parent) => (
                                <div key={parent.id} className="space-y-1">
                                  <Link
                                    to={`/products?category=${encodeURIComponent(parent.name)}`}
                                    onClick={() => setMenuOpen(false)}
                                    className="block font-display text-sm font-bold uppercase tracking-tight text-brand-ink hover:text-brand-red"
                                  >
                                    {formatCategoryName(parent.name)}
                                  </Link>
                                  {parent.subcategories && parent.subcategories.length > 0 && (
                                    <div className="pl-3 space-y-1 border-l border-black/10">
                                      {parent.subcategories.map((sub) => (
                                        <Link
                                          key={sub.id}
                                          to={`/products?category=${encodeURIComponent(sub.name)}`}
                                          onClick={() => setMenuOpen(false)}
                                          className="block text-xs text-neutral-500 hover:text-brand-red py-0.5"
                                        >
                                          {formatCategoryName(sub.name)}
                                        </Link>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    }

                    return (
                      <Link
                        key={l.to}
                        to={l.to}
                        target={l.openInNewTab ? "_blank" : undefined}
                        onClick={() => setMenuOpen(false)}
                        data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                        className="flex items-baseline gap-4 border-b border-black/5 py-4 font-display text-2xl font-medium tracking-tight hover:text-brand-red"
                      >
                        <span className="font-mono text-xs text-brand-red">0{i + 1}</span>
                        {l.label}
                      </Link>
                    );
                  })}
                  <Link
                    to="/inquiry"
                    onClick={() => setMenuOpen(false)}
                    data-testid="mobile-nav-inquiry"
                    className="btn-primary mt-8"
                  >
                    Send Inquiry
                  </Link>
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>
      <SearchDialog open={searchOpen} onOpenChange={setSearchOpen} />
    </>
  );
};

export default Navbar;

